import React from "react";
import { motion } from "framer-motion";
import { FiAlertCircle, FiLayers, FiMaximize2, FiMinimize2 } from "react-icons/fi";
import { FaTooth } from "react-icons/fa6";
import { GiIncisors, GiTooth } from "react-icons/gi";
import { RiToothFill, RiToothLine } from "react-icons/ri";
import { TbStack2 } from "react-icons/tb";
import ToothField from "./ToothField";
import SelectInput from "./SelectInputNew";
import TextInput from "./TextInputNew";
import {
  CrownBridgeForm,
  ImplantForm,
  BlueCardProps,
} from "../../../types/BlueCardTypes";
import { useAppSelector } from "../../../store/hooks";
import { getMaterialIcon } from "./formIcons";
import { isVelvetLabToken } from "../../../utils/brandLogo";

const prosthesisIcon = (value: string) => {
  const v = value.toLowerCase();
  if (v.includes("veneer")) return <RiToothFill className="h-4 w-4" />;
  if (v.includes("maryland")) return <TbStack2 className="h-4 w-4" />;
  if (v.includes("bridge")) return <GiIncisors className="h-4 w-4" />;
  if (v.includes("crown")) return <GiTooth className="h-4 w-4" />;
  if (v.includes("inlay")) return <RiToothLine className="h-4 w-4" />;
  if (v.includes("onlay")) return <FaTooth className="h-4 w-4" />;
  return <FaTooth className="h-4 w-4" />;
};

const FieldGroup = ({
  title,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <div>
    <h4 className="mb-3 text-xs font-medium text-[var(--cu-muted)]">{title}</h4>
    {children}
  </div>
);

const BlueCard: React.FC<BlueCardProps> = ({
  mode,
  cardData,
  onChange,
  errors,
  filterToken,
}) => {
  const { card } = cardData;
  const { user } = useAppSelector((state) => state.auth);

  const prosthesisOptions =
    mode === "crownBridge"
      ? [
          { value: "Inlay", label: "Inlay", icon: prosthesisIcon("Inlay") },
          {
            value: "Onlay / overlay",
            label: "Onlay / overlay",
            icon: prosthesisIcon("Onlay"),
          },
          { value: "Crown", label: "Crown", icon: prosthesisIcon("Crown") },
          { value: "Bridge", label: "Bridge", icon: prosthesisIcon("Bridge") },
          {
            value: "Maryland / Resin bonded bridge",
            label: "Maryland / Resin bonded bridge",
            icon: prosthesisIcon("Maryland"),
          },
          {
            value: "Veneer",
            label: "Veneer",
            icon: prosthesisIcon("Veneer"),
          },
        ]
      : [
          { value: "Crown", label: "Crown", icon: prosthesisIcon("Crown") },
          { value: "Bridge", label: "Bridge", icon: prosthesisIcon("Bridge") },
        ];

  const designTypeOptions = [
    {
      value: "Full Contour",
      label: "Full Contour",
      icon: <FiMaximize2 className="h-4 w-4" />,
    },
    {
      value: "Facial cutback",
      label: "Facial cutback",
      icon: <FiMinimize2 className="h-4 w-4" />,
    },
    {
      value: "Coping",
      label: "Coping",
      icon: <FiLayers className="h-4 w-4" />,
    },
  ];

  const materialOptions =
    user?.partner === "elemdig"
      ? [
          { value: "3M", label: "3M" },
          { value: "Argen HT+", label: "Argen HT+" },
          { value: "Argen Z Ultra", label: "Argen Z Ultra" },
          { value: "Bio HPP/Peek", label: "Bio HPP/Peek" },
          { value: "Brecam", label: "Brecam" },
          { value: "Celtra Press", label: "Celtra Press" },
          { value: "Coltene", label: "Coltene" },
          { value: "Dental Direct", label: "Dental Direct" },
          { value: "GC Cerasmart", label: "GC Cerasmart" },
          { value: "Katana", label: "Katana" },
          { value: "Luxor", label: "Luxor" },
          { value: "Milled Composite", label: "Milled Composite" },
          { value: "Milled Emax", label: "Milled Emax" },
          { value: "Pmma", label: "Pmma" },
          { value: "Pressed E.Max", label: "Pressed E.Max" },
          { value: "Pressed Livento", label: "Pressed Livento" },
          { value: "Sagemax", label: "Sagemax" },
          { value: "Vita Enamic", label: "Vita Enamic" },
          { value: "Wax", label: "Wax" },
          { value: "Whitepeaks Symphony", label: "Whitepeaks Symphony" },
          { value: "ZirCAD Prime", label: "ZirCAD Prime" },
          { value: "ZirCAD Prime Esthetic", label: "ZirCAD Prime Esthetic" },
        ].map((o) => ({ ...o, icon: getMaterialIcon(o.value) }))
      : [
          { value: "Composite", label: "Composite" },
          { value: "Emax", label: "Emax" },
          { value: "Zirconia", label: "Zirconia" },
          {
            value: "Metal (precious/non-precious)",
            label: "Metal (precious/non-precious)",
          },
          { value: "Acrylic / PMMA", label: "Acrylic / PMMA" },
        ].map((o) => ({ ...o, icon: getMaterialIcon(o.value) }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-3.5 md:p-4"
    >
      <div className="space-y-4">
        {(mode === "crownBridge" || mode === "implant") && (
          <FieldGroup title="Tooth selection">
            <ToothField
              label="Tooth #s"
              hint="Including pontics"
              toothNumbers={card.toothNumbers}
              onToothClick={(tooth) => {
                const toothStr = tooth.toString();
                const updated = card.toothNumbers.includes(toothStr)
                  ? card.toothNumbers.filter((t) => t !== toothStr)
                  : [...card.toothNumbers, toothStr];

                onChange("toothNumbers", updated);
              }}
            />

            {errors?.toothNumbers && (
              <p className="mt-1 flex items-center gap-1 text-xs text-[var(--cu-danger)]">
                <FiAlertCircle className="h-3 w-3 shrink-0" />
                {errors.toothNumbers}
              </p>
            )}
          </FieldGroup>
        )}

        {mode === "crownBridge" && (
          <FieldGroup title="Restoration specs">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectInput
                stacked
                label="Material"
                name="material"
                value={(card as CrownBridgeForm).material}
                onChange={(e) => onChange("material", e.target.value)}
                options={[
                  ...materialOptions,
                  ...(isVelvetLabToken(filterToken)
                    ? [{ value: "Print", label: "Print" }]
                    : []),
                ]}
              />

              <SelectInput
                stacked
                label="Prosthesis Type"
                name="prosthesisType"
                value={(card as CrownBridgeForm).prosthesisType}
                onChange={(e) => onChange("prosthesisType", e.target.value)}
                options={prosthesisOptions}
              />

              <SelectInput
                stacked
                label="Design Type"
                name="designType"
                value={(card as CrownBridgeForm).designType}
                onChange={(e) => onChange("designType", e.target.value)}
                options={designTypeOptions}
              />

              <TextInput
                stacked
                label="Shade"
                name="shade"
                optional
                value={(card as CrownBridgeForm).shade}
                onChange={(e) => onChange("shade", e.target.value)}
              />
            </div>
          </FieldGroup>
        )}

        {mode === "implant" && (
          <>
            {isVelvetLabToken(filterToken) ? (
              <FieldGroup title="Implant Protocol">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <SelectInput
                    stacked
                    label="Restoration required"
                    name="restorationRequired"
                    value={(card as ImplantForm).restorationRequired}
                    onChange={(e) =>
                      onChange("restorationRequired", e.target.value)
                    }
                    options={[
                      {
                        value: "Custom abutment + Crown",
                        label: "Custom abutment + Crown",
                      },
                      {
                        value: "Custom abutment only",
                        label: "Custom abutment only",
                      },
                      { value: "Screw retained", label: "Screw retained" },
                    ]}
                  />

                  <SelectInput
                    stacked
                    label="Prosthesis Type"
                    name="prosthesisType"
                    value={(card as ImplantForm).prosthesisType}
                    onChange={(e) => onChange("prosthesisType", e.target.value)}
                    options={prosthesisOptions}
                  />

                  <SelectInput
                    stacked
                    label="Implant System"
                    name="implantSystem"
                    value={(card as ImplantForm).implantSystem}
                    onChange={(e) => onChange("implantSystem", e.target.value)}
                    options={[
                      { value: "IPD Biohorizon", label: "IPD Biohorizon" },
                      { value: "Tri Matrix", label: "Tri Matrix" },
                      { value: "Other", label: "Other" },
                    ]}
                  />

                  <TextInput
                    stacked
                    label="Implant Size"
                    name="implantSize"
                    value={(card as ImplantForm).implantSize}
                    onChange={(e) => onChange("implantSize", e.target.value)}
                  />

                  <SelectInput
                    stacked
                    label="Connection Type"
                    name="connectionType"
                    value={(card as ImplantForm).connectionType}
                    onChange={(e) => onChange("connectionType", e.target.value)}
                    options={[
                      {
                        value: "Custom Interface",
                        label: "Custom Interface",
                      },
                      {
                        value: "Direct Interface",
                        label: "Direct Interface",
                      },
                    ]}
                  />

                  <SelectInput
                    stacked
                    label="Scan Flag Type"
                    name="scanFlagType"
                    value={(card as ImplantForm).scanFlagType}
                    onChange={(e) => onChange("scanFlagType", e.target.value)}
                    options={[
                      { value: "Engaging", label: "Engaging" },
                      { value: "Non-Engaging", label: "Non-Engaging" },
                      { value: "Multi", label: "Multi" },
                    ]}
                  />
                </div>

                <details className="mt-2 rounded-lg border border-[var(--cu-border)] bg-[var(--cu-input)]">
                  <summary className="cursor-pointer list-none px-3 py-2 text-xs text-[var(--cu-muted)] marker:content-none [&::-webkit-details-marker]:hidden">
                    Optional refinements
                    <span className="ml-1 text-[var(--cu-subtle)]">— expand</span>
                  </summary>
                  <div className="grid grid-cols-1 gap-3 border-t border-[var(--cu-border)] px-3 pb-2 pt-3 sm:grid-cols-2 lg:grid-cols-3">
                    <SelectInput
                      stacked
                      optional
                      label="Tissue pressure"
                      name="tissuePressure"
                      value={(card as ImplantForm).tissuePressure}
                      onChange={(e) =>
                        onChange("tissuePressure", e.target.value)
                      }
                      options={[
                        {
                          value: "Zero (Modified Ridge Lap)",
                          label: "Zero (Modified Ridge Lap)",
                        },
                        { value: "Ovate", label: "Ovate" },
                      ]}
                    />

                    <SelectInput
                      stacked
                      optional
                      label="Screw hole Diameter"
                      name="screwHoleDiameter"
                      value={(card as ImplantForm).screwHoleDiameter}
                      onChange={(e) =>
                        onChange("screwHoleDiameter", e.target.value)
                      }
                      options={[
                        { value: "Default", label: "Default" },
                        { value: "3mm", label: "3mm" },
                      ]}
                    />

                    <SelectInput
                      stacked
                      optional
                      label="Screw hole angulation"
                      name="screwHoleAngulation"
                      value={(card as ImplantForm).screwHoleAngulation}
                      onChange={(e) =>
                        onChange("screwHoleAngulation", e.target.value)
                      }
                      options={[
                        { value: "0-15", label: "0-15" },
                        { value: "More than 15", label: "More than 15" },
                      ]}
                    />

                    <SelectInput
                      stacked
                      optional
                      label="Bridge base width"
                      name="bridgeBaseWidth"
                      value={(card as ImplantForm).bridgeBaseWidth}
                      onChange={(e) =>
                        onChange("bridgeBaseWidth", e.target.value)
                      }
                      options={[
                        {
                          value: "As per pontic natural size",
                          label: "As per pontic natural size",
                        },
                        { value: "8-10mm", label: "8-10mm" },
                        { value: "12-13mm", label: "12-13mm" },
                      ]}
                    />

                    <SelectInput
                      stacked
                      optional
                      label="All on X design with gingiva"
                      name="allOnXDesignWithGingiva"
                      value={(card as ImplantForm).allOnXDesignWithGingiva}
                      onChange={(e) =>
                        onChange("allOnXDesignWithGingiva", e.target.value)
                      }
                      options={[
                        { value: "Needs gingiva", label: "Needs gingiva" },
                        {
                          value: "Does not need gingiva",
                          label: "Does not need gingiva",
                        },
                      ]}
                    />

                    <SelectInput
                      stacked
                      optional
                      label="Model gingiva for all on X cases"
                      name="modelGingivaForAllOnXCases"
                      value={(card as ImplantForm).modelGingivaForAllOnXCases}
                      onChange={(e) =>
                        onChange("modelGingivaForAllOnXCases", e.target.value)
                      }
                      options={[
                        { value: "One piece", label: "One piece" },
                        { value: "Multiple pieces", label: "Multiple pieces" },
                      ]}
                    />
                  </div>
                </details>
              </FieldGroup>
            ) : (
              <FieldGroup title="Implant Specs">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <TextInput
                    stacked
                    label="Scan body manufacturer"
                    name="scanBodyManufacturer"
                    value={(card as ImplantForm).scanBodyManufacturer}
                    onChange={(e) =>
                      onChange("scanBodyManufacturer", e.target.value)
                    }
                  />
                  <TextInput
                    stacked
                    optional
                    label="Scan body code"
                    name="scanBodyCode"
                    value={(card as ImplantForm).scanBodyCode}
                    onChange={(e) => onChange("scanBodyCode", e.target.value)}
                  />
                  <TextInput
                    stacked
                    label="Implant manufacturer"
                    name="implantManufacturer"
                    value={(card as ImplantForm).implantManufacturer}
                    onChange={(e) =>
                      onChange("implantManufacturer", e.target.value)
                    }
                  />
                  <TextInput
                    stacked
                    label="Platform diameter"
                    name="platformDiameter"
                    value={(card as ImplantForm).platformDiameter}
                    onChange={(e) =>
                      onChange("platformDiameter", e.target.value)
                    }
                  />
                  <SelectInput
                    stacked
                    label="Restoration required"
                    name="restorationRequired"
                    value={(card as ImplantForm).restorationRequired}
                    onChange={(e) =>
                      onChange("restorationRequired", e.target.value)
                    }
                    options={[
                      {
                        value: "Custom abutment + Crown",
                        label: "Custom abutment + Crown",
                      },
                      {
                        value: "Custom abutment only",
                        label: "Custom abutment only",
                      },
                      { value: "Screw retained", label: "Screw retained" },
                    ]}
                  />
                  <SelectInput
                    stacked
                    label="Prosthesis Type"
                    name="prosthesisType"
                    value={(card as ImplantForm).prosthesisType}
                    onChange={(e) => onChange("prosthesisType", e.target.value)}
                    options={prosthesisOptions}
                  />
                  <SelectInput
                    stacked
                    label="Material"
                    name="material"
                    value={(card as ImplantForm).material}
                    onChange={(e) => onChange("material", e.target.value)}
                    options={materialOptions}
                  />
                  <TextInput
                    stacked
                    label="Shade"
                    name="shade"
                    optional
                    value={(card as ImplantForm).shade}
                    onChange={(e) => onChange("shade", e.target.value)}
                  />
                </div>
              </FieldGroup>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default BlueCard;
